import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { IMG } from "../lib/media";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextParam = params.get("next");
  const destFor = (u) => nextParam || (u && (u.role === "admin" || u.role === "staff") ? "/admin" : "/dashboard");
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(destFor(user), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success("Welcome back!");
      navigate(destFor(u));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative">
        <img src={IMG.injectables} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/20" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-accent text-3xl italic text-white">"Your glow, always within reach."</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex mb-10">
            <img
              src={`${process.env.PUBLIC_URL}/logo-transparent.png`}
              alt="Iridescent Medical Spa"
              className="h-10 w-auto"
            />
          </Link>
          <h1 className="font-heading text-3xl font-light text-ink">Member login</h1>
          <p className="text-[#5C6664] mt-2 text-sm">Access your membership, points and rewards.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-ink">Email</Label>
              <Input id="email" type="email" required value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="login-email" className="mt-2 rounded-lg h-11" placeholder="you@email.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-ink">Password</Label>
              <Input id="password" type="password" required value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                data-testid="login-password" className="mt-2 rounded-lg h-11" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-500" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="iri-gradient text-ink rounded-full h-12 w-full font-medium hover:scale-[1.02] transition-transform">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-[#5C6664]">
            New here?{" "}
            <Link to="/register" className="text-ink font-medium underline" data-testid="go-to-register">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
